import { Grid, GridRow } from '@bp/ui-components';
import { Form, Formik, FormikHelpers } from 'formik';
import { useTranslation } from 'react-i18next';
import { LessonEditStatus } from '../../../types/planung-graphql-client-defs';
import { showSuccessCreateToast, showSuccessUpdateToast, showUserDbErrorToast } from '../../../utils/toast';
import { LessonBasicsForm } from '../../Lessons/Forms/Basics/LessonBasicsForm';
import { LessonRoomSupplyForm } from '../../Lessons/Forms/RoomSupply/LessonRoomSupplyForm';
import { ModalBottomButtons } from '../../ModalBottomButtons/ModalBottomButtons';
import { toCurriculumFormValues, useCurriculum } from '../hooks/useCurriculum';
import { CurriculumFormType } from '../types';
import { CurriculumClassesForm } from './Classes/CurriculumClassesForm';
import { CurriculumUnitForm } from './LessonUnit/CurriculumUnitForm';
import { CurriculumTeachersForm } from './Teachers/CurriculumTeachersForm';
import { schema } from './validation/schema';

type CurriculumFormProps = {
  versionUuid: string | null;
  curriculumUuid: string | null;
  defaultInitialValues: CurriculumFormType;
  onLoad: (loading: boolean) => void;
  onClose: () => void;
};

export const CurriculumForm = ({
  versionUuid,
  curriculumUuid,
  onLoad,
  defaultInitialValues,
  onClose,
}: CurriculumFormProps) => {
  const { t } = useTranslation();

  const { queryData, updateCurriculum, createCurriculum } = useCurriculum({
    versionUuid: versionUuid ?? '',
    grade: undefined,
  });

  const curriculum = queryData?.curricula.find((c) => c.uuid === curriculumUuid);

  const initialValues: CurriculumFormType = curriculum
    ? {
        ...toCurriculumFormValues(curriculum),
        curriculumVersionUuid: defaultInitialValues.curriculumVersionUuid,
      }
    : { ...defaultInitialValues, lessonUnit: [] };

  const handleSubmit = async (values: CurriculumFormType, formikHelpers: FormikHelpers<CurriculumFormType>) => {
    onLoad(true);

    if (curriculum) {
      const { error } = await updateCurriculum({ curriculumUuid: curriculum.uuid, values });
      if (error) {
        showUserDbErrorToast();
      } else {
        showSuccessUpdateToast();
      }
    } else {
      const { error } = await createCurriculum(values);
      if (error) {
        showUserDbErrorToast();
      } else {
        showSuccessCreateToast();
      }
    }

    onLoad(false);
    formikHelpers.resetForm();
    onClose();
  };

  return (
    <Formik initialValues={initialValues} onSubmit={handleSubmit} validationSchema={schema} validateOnChange>
      {({ resetForm, errors, dirty, isSubmitting }) => (
        <Form>
          <Grid useFormGap>
            <GridRow>
              <LessonBasicsForm status={LessonEditStatus.Unused} />
            </GridRow>

            <GridRow headline={t('classes.title', { count: 2 })}>
              <CurriculumClassesForm enableGroups={true} selectableGrades={[]} />
            </GridRow>

            <GridRow headline={t('lessonUnit.title', { count: 1 })}>
              <CurriculumUnitForm />
            </GridRow>

            <GridRow headline={t('persons.title', { count: 1 })}>
              <CurriculumTeachersForm />
            </GridRow>

            <GridRow headline={t('rooms.stock')}>
              <LessonRoomSupplyForm disabled={false} />
            </GridRow>
          </Grid>

          <ModalBottomButtons
            closeButton={{
              text: t('common.cancel'),
              callback: () => {
                resetForm();
                onClose();
              },
            }}
            submitButton={{
              disabled: Object.keys(errors).length > 0 || !dirty,
            }}
            isLoading={isSubmitting}
            errors={errors}
          />
        </Form>
      )}
    </Formik>
  );
};
